import React from 'react';
import _ from 'lodash';
import { decorate, computed, action, observable, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container, Icon } from 'semantic-ui-react';

import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import {
  isStoreLoading,
  isStoreEmpty,
  isStoreNotEmpty,
  isStoreError,
  isStoreReady,
} from '@amzn/base-ui/dist/models/BaseStore';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

import CompactTable from './CompactTable';
import FilterBox from './FilterBox';
import ActionButtons from './ActionButtons';
import EnvsHeader from '../ScEnvsHeader';
import Paginate from '../../helpers/Paginate';

const statusMap = [
  { name: 'ACTIVE', list: ['COMPLETED', 'COMPLETED_EXPIRED', 'TAINTED', 'RESTARTING', 'STOPPED', 'INSUFFICIENT_FUNDS', 'EXPIRED', 'PENDING', 'TERMINATING', 'STARTING', 'STOPPING', 'RESTARTING', 'TERMINATING_FAILED', 'STARTING_FAILED', 'STOPPING_FAILED'] },
  { name: 'RUNNING', list: ['COMPLETED', 'COMPLETED_EXPIRED', 'TAINTED', 'RESTARTING'] },
  { name: 'PENDING', list: ['PENDING', 'TERMINATING', 'STARTING', 'STOPPING', 'RESTARTING'] },
  { name: 'STOPPED', list: ['STOPPED', 'INSUFFICIENT_FUNDS', 'EXPIRED'] },
  { name: 'EXPIRED', list: ['EXPIRED', 'COMPLETED_EXPIRED'] },
  { name: 'ERRORED', list: ['FAILED', 'TERMINATING_FAILED', 'STARTING_FAILED', 'STOPPING_FAILED'] },
  { name: 'TERMINATED', list: ['TERMINATED'] },
];

class ScEnvAdvancedList extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.goto = gotoFn(this);
      this.page = 1;
      this.sorts = [{ field: 'status', desc: false }];
    });
  }

  handlePaginationChange() {
    return number =>
      runInAction(() => {
        this.page = number;
        window.scrollTo(0, 0);
      });
  }

  handlePerPageChange() {
    return number =>
      runInAction(() => {
        this.page = 1;
        this.viewStore.setPerPage(number);
      });
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    swallowError(this.envsStore.load());
    this.envsStore.startHeartbeat();

    if (!isStoreReady(this.envTypesStore)) {
      swallowError(this.envTypesStore.load());
    }
  }

  get envTypesStore() {
    return this.props.envTypesStore;
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  get userDisplayName() {
    return this.props.userDisplayName;
  }

  get viewStore() {
    return this.props.ScEnvView;
  }

  userName(env) {
    return this.props.userDisplayName.getLongDisplayName({ uid: env.createdBy });
  }

  workspaceName(env) {
    return this.props.envTypesStore.getEnvType(env.envTypeId)?.name;
  }

  statusName(envStatus) {
    return _.find(statusMap, ({ list }) => list.includes(envStatus))?.name || 'UNKNOWN';
  }

  getStatusForFiltering(envStatus, filterValue) {
    if (filterValue.startsWith('INDIVIDUAL_')) {
      const individualStatus = filterValue.replace('INDIVIDUAL_', '');
      return envStatus === individualStatus ? individualStatus : null;
    }
    return this.statusName(envStatus);
  }

  canDoAction(env, actionType) {
    return this.envsStore.canChangeState(env.id) && env.state[actionType];
  }

  getEnvFields(envs) {
    const optionSets = envs.reduce(
      (opts, env) => {
        opts.user.add(this.userName(env));
        opts.project.add(env.projectId);
        env.studyIds.forEach(study => opts.study.add(study));
        opts.type.add(this.workspaceName(env) || env.envTypeId);
        opts.configType.add(env.envTypeConfigId);
        return opts;
      },
      {
        user: new Set(),
        project: new Set(),
        study: new Set(),
        type: new Set(),
        configType: new Set(),
      },
    );
    const options = _.mapValues(optionSets, set => Array.from(set).map(option => ({ text: option, value: option })));
    const categoryOptions = statusMap.map(({ name }) => ({ text: name, value: name }));
    const individualStatuses = ['COMPLETED', 'COMPLETED_EXPIRED', 'TAINTED', 'STOPPED', 'INSUFFICIENT_FUNDS', 'EXPIRED', 'PENDING', 'TERMINATING', 'STARTING', 'STOPPING', 'RESTARTING', 'FAILED', 'TERMINATING_FAILED', 'STARTING_FAILED', 'STOPPING_FAILED', 'TERMINATED'];
    const individualOptions = individualStatuses.map(status => ({ text: status, value: `INDIVIDUAL_${status}` }));
    const statusOptions = [
      { text: '--- Categories ---', value: '', disabled: true },
      ...categoryOptions,
      { text: '--- Individual ---', value: '', disabled: true },
      ...individualOptions
    ];

    const fields = [
      { key: 'id', label: 'ID', icon: 'hashtag', hidden: true },
      { key: 'name', label: 'Name', icon: 'info' },
      { key: 'user', label: 'Created By', icon: 'user', options: options.user },
      { key: 'createdAt', label: 'Created At', icon: 'calendar alternate outline', type: 'date', filterable: false },
      { key: 'project', label: 'Project', icon: 'suitcase', options: options.project },
      { key: 'study', label: 'Studies', icon: 'database', options: options.study, sortable: false },
      { key: 'type', label: 'Workspace Type', icon: 'laptop', options: options.type },
      { key: 'configType', label: 'Workspace Config', icon: 'setting', options: options.configType },
      { key: 'status', label: 'Status', icon: 'cloud', options: statusOptions },
    ];
    return fields;
  }

  getFilteredEnvs() {
    let envRows = _(this.envsStore.list).map(env => ({
      id: env.id,
      name: env.name,
      user: this.userName(env),
      createdAt: env.createdAt,
      project: env.projectId,
      study: env.studyIds?.join(', ') || 'none',
      type: this.workspaceName(env) || env.envTypeId,
      configType: env.envTypeConfigId,
      status: env.status, // Show actual workspace status, not mapped category
      actions: (
        <ActionButtons
          id={env.id}
          pending={this.statusName(env.status) === 'PENDING'}
          terminationLocked={env.terminationLocked}
          can={{
            start: this.canDoAction(env, 'canStart'),
            stop: this.canDoAction(env, 'canStop'),
            terminate: this.canDoAction(env, 'canTerminate'),
            lock: this.canDoAction(env, 'canTerminate'), // only allow lock/unlock if the instance can be terminated
          }}
          onAction={this.handleAction()}
        />
      ),
    }));

    if (this.viewStore.filters.length > 0) {
      const or = (list, predicate) => _.find(list, predicate);
      const and = (list, predicate) => _.reduce(list, (acc, value) => acc && predicate(value), true);
      const listMatcher = (A, b) => _.find(A, a => a === b);
      const regexMatcher = (a, b) => new RegExp(_.escapeRegExp(a), 'i').test(b);

      envRows = envRows.filter(env => {
        const operator = this.viewStore.mode === 'or' ? or : and;
        return operator(this.viewStore.filters, ({ key, value, match }) => {
          const matcher = match === 'partial' ? regexMatcher : listMatcher;
          if (key === 'status') {
            const envFromStore = this.envsStore.list.find(e => e.id === env.id);
            return value.some(filterValue => {
              if (filterValue.startsWith('INDIVIDUAL_')) {
                // Individual status filtering - exact match
                const individualStatus = filterValue.replace('INDIVIDUAL_', '');
                return envFromStore.status === individualStatus;
              } else {
                // Category filtering - check if actual status is in category's list
                const category = statusMap.find(cat => cat.name === filterValue);
                return category && category.list.includes(envFromStore.status);
              }
            });
          }
          return matcher(value, env[key]);
        });
      });
    }

    const sortFields = this.sorts.map(s => s.field);
    const sortOrders = this.sorts.map(s => (s.desc ? 'desc' : 'asc'));
    return envRows.orderBy(sortFields, sortOrders).value();
  }

  getPaginatedEnvs() {
    const filteredEnvs = this.getFilteredEnvs();

    const firstIndex = (this.page - 1) * this.viewStore.perPage;
    const lastIndex = Math.min(this.page * this.viewStore.perPage, filteredEnvs.length);
    return {
      paginatedEnvList: filteredEnvs.slice(firstIndex, lastIndex),
      filteredEnvsCount: filteredEnvs.length,
    };
  }

  handleAction() {
    const actionMap = {
      start: this.envsStore.startScEnvironment,
      stop: this.envsStore.stopScEnvironment,
      terminate: this.envsStore.terminateScEnvironment,
      toggleLock: this.envsStore.toggleScEnvironmentLock,
      view: this.goto,
    };
    return (actionType, value) => actionMap[actionType](value);
  }

  handleFilter() {
    return ({ filters, mode }) =>
      runInAction(() => {
        this.page = 1;
        this.viewStore.setFilters(filters, mode);
      });
  }

  handleSort() {
    return (key, e) => runInAction(() => {
      const isCtrl = e && (e.ctrlKey || e.metaKey);
      const existing = this.sorts.findIndex(s => s.field === key);
      if (isCtrl) {
        if (existing >= 0) {
          this.sorts[existing] = { field: key, desc: !this.sorts[existing].desc };
        } else {
          this.sorts.push({ field: key, desc: false });
        }
        this.sorts = [...this.sorts]; // trigger reactivity
      } else {
        if (this.sorts.length === 1 && existing === 0) {
          this.sorts = [{ field: key, desc: !this.sorts[0].desc }];
        } else {
          this.sorts = [{ field: key, desc: false }];
        }
      }
    });
  }

  handleViewToggle() {
    return () => runInAction(() => this.viewStore.toggleView());
  }

  handleCompactToggle() {
    return () => runInAction(() => this.viewStore.toggleCompactMode());
  }

  handleCreateEnvironment() {
    return event => {
      event.preventDefault();
      event.stopPropagation();

      this.goto(`/workspaces/create`);
    };
  }

  render() {
    const store = this.envsStore;
    let content = null;
    let total = 0;
    let current = 0;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="p0" />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder segmentCount={3} />;
    } else if (isStoreEmpty(store)) {
      content = (
        <div className="workspace-empty-state" data-testid="workspaces">
          <div className="workspace-empty-state-icon workspace-empty-state-icon-primary">
            <Icon name="server" />
          </div>
          <div className="workspace-empty-state-title">No workspaces</div>
          <div className="workspace-empty-state-subtitle">To create a workspace, click Create Workspace.</div>
        </div>
      );
    } else if (isStoreNotEmpty(store)) {
      const fields = this.getEnvFields(store.list);
      const tableColumns = fields
        .filter(({ hidden = false }) => !hidden)
        .map(column => _.pick(column, ['key', 'label', 'sortable', 'type']));
      const { paginatedEnvList, filteredEnvsCount } = this.getPaginatedEnvs();
      current = filteredEnvsCount;
      total = store.total;

      content = (
        <>
          <FilterBox
            mode={this.viewStore.mode}
            filters={this.viewStore.filters}
            fields={fields}
            onFilter={this.handleFilter()}
          />
          <Paginate
            entriesPerPage={this.viewStore.perPage}
            totalEntries={filteredEnvsCount}
            currentPage={this.page}
            onPageChange={this.handlePaginationChange()}
            onPerPageChange={this.handlePerPageChange()}
          >
            <CompactTable
              sorts={this.sorts}
              columns={tableColumns}
              rows={paginatedEnvList}
              onSort={this.handleSort()}
            />
          </Paginate>
        </>
      );
    }

    return (
      <Container className="mt3 animated fadeIn">
        <EnvsHeader
          view={this.viewStore.view}
          isAdmin // We only get to this view if we're an admin, so this must be true
          compactMode={this.viewStore.compactMode}
          onViewToggle={this.handleViewToggle()}
          onCompactToggle={this.handleCompactToggle()}
        />
        {content}
      </Container>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(ScEnvAdvancedList, {
  viewStore: computed,
  envsStore: computed,
  envTypesStore: computed,
  userDisplayName: computed,

  handleFilter: action,
  handleSort: action,
  handleViewToggle: action,
  handleCompactToggle: action,
  handleCreateEnvironment: action,
  handlePaginationChange: action,
  handlePerPageChange: action,

  page: observable,
  sorts: observable,
});

export default inject(
  'ScEnvView',
  'scEnvironmentsStore',
  'userDisplayName',
  'envTypesStore',
)(withRouter(observer(ScEnvAdvancedList)));
